import React from 'react';
import './Card.css';

export type CardVariant = 'default' | 'elevated' | 'outlined' | 'surface';
export type CardPadding = 'none' | 'sm' | 'md' | 'lg';

export interface CardProps {
  /** Card content */
  children: React.ReactNode;
  /** Visual variant */
  variant?: CardVariant;
  /** Content padding */
  padding?: CardPadding;
  /** Header content (title, actions, etc.) */
  header?: React.ReactNode;
  /** Footer content (buttons, links, etc.) */
  footer?: React.ReactNode;
  /** Cover image URL (displayed above header) */
  coverImage?: string;
  /** Cover image alt text */
  coverImageAlt?: string;
  /** Makes the entire card clickable */
  onClick?: () => void;
  /** URL for card link (alternative to onClick) */
  href?: string;
  /** Custom CSS class */
  className?: string;
  /** Accessible label */
  'aria-label'?: string;
  /** Test ID for testing */
  'data-testid'?: string;
}

export const Card: React.FC<CardProps> = ({
  children,
  variant = 'default',
  padding = 'md',
  header,
  footer,
  coverImage,
  coverImageAlt = '',
  onClick,
  href,
  className = '',
  'aria-label': ariaLabel,
  'data-testid': testId,
}) => {
  const isInteractive = Boolean(onClick || href);

  const classes = [
    'hi-ui-card',
    `hi-ui-card--${variant}`,
    `hi-ui-card--padding-${padding}`,
    isInteractive ? 'hi-ui-card--interactive' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const content = (
    <>
      {coverImage && (
        <div className="hi-ui-card__cover">
          <img src={coverImage} alt={coverImageAlt} className="hi-ui-card__cover-image" />
        </div>
      )}
      {header && <div className="hi-ui-card__header">{header}</div>}
      <div className="hi-ui-card__body">{children}</div>
      {footer && <div className="hi-ui-card__footer">{footer}</div>}
    </>
  );

  // Render as link if href provided
  if (href) {
    return (
      <a href={href} className={classes} aria-label={ariaLabel} data-testid={testId}>
        {content}
      </a>
    );
  }

  // Render as button if onClick provided
  if (onClick) {
    return (
      <div
        className={classes}
        onClick={onClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick();
          }
        }}
        role="button"
        tabIndex={0}
        aria-label={ariaLabel}
        data-testid={testId}
      >
        {content}
      </div>
    );
  }

  // Render as static div
  return (
    <div className={classes} aria-label={ariaLabel} data-testid={testId}>
      {content}
    </div>
  );
};

// Convenience sub-component for structured headers
export interface CardTitleProps {
  /** Title text */
  children: React.ReactNode;
  /** Actions to display alongside title */
  actions?: React.ReactNode;
  /** Custom CSS class */
  className?: string;
}

export const CardTitle: React.FC<CardTitleProps> = ({ children, actions, className = '' }) => (
  <div className={`hi-ui-card__title-row ${className}`.trim()}>
    <h3 className="hi-ui-card__title">{children}</h3>
    {actions && <div className="hi-ui-card__title-actions">{actions}</div>}
  </div>
);

// Convenience sub-component for footer actions
export interface CardActionsProps {
  /** Action buttons/links */
  children: React.ReactNode;
  /** Alignment of actions */
  align?: 'start' | 'center' | 'end' | 'between';
  /** Custom CSS class */
  className?: string;
}

export const CardActions: React.FC<CardActionsProps> = ({
  children,
  align = 'end',
  className = '',
}) => (
  <div className={`hi-ui-card__actions hi-ui-card__actions--${align} ${className}`.trim()}>
    {children}
  </div>
);
